import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Inbox, RefreshCw, LogOut } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { InboxDetail } from '../components/inbox/InboxDetail';
import { STATUS_OPTIONS, StatusBadge } from '../components/inbox/StatusBadge';
import { InboxLogin } from '../components/inbox/InboxLogin';
import { ProductsAdmin } from '../components/inbox/ProductsAdmin';
import { adminFetch, AuthError, checkAdminSession, clearAdminToken } from '../components/inbox/adminApi';

const KIND_LABEL = { quote: 'Quick Quote', questionnaire: 'Questionnaire', order: 'Order' };

const fetchInboxItems = async (kind, status) => {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (status) params.set('status', status);
  const res = await adminFetch(`/api/inbox/items?${params}`);
  return res.json();
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const FilterSelect = ({ id, value, onChange, children }) => (
  <select id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} style={{ padding: '0.6rem 0.9rem', borderRadius: '8px', border: '1px solid var(--border-medium)', backgroundColor: 'var(--bg-card)', fontFamily: 'inherit' }}>
    {children}
  </select>
);

const InboxPage = () => {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(null);
  const [view, setView] = useState('requests');

  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  const signOut = () => {
    clearAdminToken();
    setAuthed(false);
    setItems([]);
    setSelected(null);
  };

  useEffect(() => {
    let cancelled = false;
    checkAdminSession().then((ok) => { if (!cancelled) setAuthed(ok); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    setLoading(true);
    fetchInboxItems(kind, status)
      .then((data) => { if (!cancelled) { setItems(data); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setLoading(false); if (e instanceof AuthError) setAuthed(false); } });
    return () => { cancelled = true; };
  }, [kind, status, reloadKey, authed]);

  const onStatusChanged = (summary) => {
    setItems((prev) => prev.map((i) => (i.id === summary.id && i.kind === summary.kind ? summary : i)));
    setSelected(summary);
  };

  return (
    <div>
      <Helmet><title>Inbox | Legal Document Specialist</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Header />
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '140px 0 80px 0', minHeight: '100vh' }}>
        <div className="container">
          {authed === null && <p data-testid="inbox-checking" style={{ color: 'var(--text-secondary)' }}>Checking session...</p>}
          {authed === false && <InboxLogin onSuccess={() => setAuthed(true)} />}
          {authed && (<>
          <div role="tablist" style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {[['requests', 'Requests'], ['products', 'Guides & Products']].map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={view === key} data-testid={`inbox-view-${key}`} onClick={() => setView(key)} style={{ padding: '0.6rem 1.1rem', borderRadius: '999px', border: `1px solid ${view === key ? 'var(--brand-primary)' : 'var(--border-medium)'}`, backgroundColor: view === key ? 'var(--brand-primary)' : 'var(--bg-card)', color: view === key ? '#fff' : 'var(--text-primary)', cursor: 'pointer', fontWeight: '600' }}>{label}</button>
            ))}
          </div>
          {view === 'products' && <ProductsAdmin onAuthError={signOut} />}
          {view === 'requests' && (<>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
            <h1 className="heading-1" data-testid="inbox-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: 0 }}>
              <Inbox size={32} color="var(--brand-primary)" /> Request Inbox
            </h1>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <FilterSelect id="inbox-filter-kind" value={kind} onChange={setKind}>
                <option value="">All types</option>
                {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </FilterSelect>
              <FilterSelect id="inbox-filter-status" value={status} onChange={setStatus}>
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </FilterSelect>
              <button type="button" onClick={load} data-testid="inbox-refresh" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem' }}>
                <RefreshCw size={16} /> Refresh
              </button>
              <button type="button" onClick={signOut} data-testid="inbox-signout" className="btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1rem' }}>
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(0, 1fr) minmax(360px, 1.1fr)' : '1fr', gap: '2rem', alignItems: 'start' }} className="inbox-layout">
            <div style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', overflowX: 'auto' }}>
              <table data-testid="inbox-table" style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '0.92rem' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--brand-primary)', color: '#fff', textAlign: 'left' }}>
                    {['ID', 'Type', 'Service', 'Client', 'Status', 'Received'].map((h) => <th key={h} style={{ padding: '0.85rem 1rem', fontWeight: '600' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</td></tr>}
                  {!loading && items.length === 0 && <tr><td colSpan={6} data-testid="inbox-empty" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>No requests match these filters.</td></tr>}
                  {!loading && items.map((item) => {
                    const active = selected && selected.id === item.id && selected.kind === item.kind;
                    return (
                      <tr key={`${item.kind}-${item.id}`} data-testid={`inbox-row-${item.kind}-${item.id}`} onClick={() => setSelected(item)} style={{ cursor: 'pointer', borderTop: '1px solid var(--border-light)', backgroundColor: active ? 'rgba(44,90,160,0.08)' : 'transparent' }}>
                        <td style={{ padding: '0.8rem 1rem', fontWeight: '700', color: 'var(--brand-primary)' }}>#{item.id}</td>
                        <td style={{ padding: '0.8rem 1rem' }}>{KIND_LABEL[item.kind]}{item.files_count > 0 ? ` · ${item.files_count} file${item.files_count > 1 ? 's' : ''}` : ''}</td>
                        <td style={{ padding: '0.8rem 1rem' }}>{item.service_label}</td>
                        <td style={{ padding: '0.8rem 1rem' }}>{item.name}<br /><span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{item.email}</span></td>
                        <td style={{ padding: '0.8rem 1rem' }}><StatusBadge status={item.status} /></td>
                        <td style={{ padding: '0.8rem 1rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmt(item.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selected && <InboxDetail item={selected} onClose={() => setSelected(null)} onStatusChanged={onStatusChanged} onAuthError={signOut} />}
          </div>
          </>)}
          </>)}
        </div>
      </section>
      <Footer />
      <style>{`@media (max-width: 1000px) { .inbox-layout { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
};

export default InboxPage;
